import { useState, useRef, useEffect } from 'react';
import api from '../../services/api';

const MentionTextarea = ({ value, onChange, placeholder, className, rows = 4, autoFocus = false }) => {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [mentionUsers, setMentionUsers] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mentionStart, setMentionStart] = useState(-1);
  const textareaRef = useRef(null);
  const mentionListRef = useRef(null);

  // Debounce timer for search
  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (mentionSearch.length > 0) {
      // Debounce the search
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
      
      searchTimerRef.current = setTimeout(() => {
        searchUsers(mentionSearch);
      }, 300);
    } else {
      setMentionUsers([]);
    }

    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [mentionSearch]);

  const searchUsers = async (query) => {
    try {
      const response = await api.get(`/users/search?q=${encodeURIComponent(query)}`);
      setMentionUsers(response.data);
      setSelectedIndex(0);
    } catch (error) {
      console.error('Error searching users:', error);
      setMentionUsers([]);
    }
  };

  const handleTextChange = (e) => {
    const newValue = e.target.value;
    const cursorPos = e.target.selectionStart;
    
    onChange({ target: { value: newValue } });

    // Check for @ mention trigger
    const textBeforeCursor = newValue.substring(0, cursorPos);
    const lastAtIndex = textBeforeCursor.lastIndexOf('@');

    if (lastAtIndex !== -1) {
      // Check if @ is at start or preceded by whitespace
      const charBeforeAt = lastAtIndex > 0 ? textBeforeCursor[lastAtIndex - 1] : ' ';
      if (charBeforeAt === ' ' || charBeforeAt === '\n' || lastAtIndex === 0) {
        const searchText = textBeforeCursor.substring(lastAtIndex + 1);
        // Only show mentions if no space after @
        if (!searchText.includes(' ') && !searchText.includes('\n')) {
          setMentionStart(lastAtIndex);
          setMentionSearch(searchText);
          setShowMentions(true);
          return;
        }
      }
    }

    // Hide mentions if no valid @ context
    setShowMentions(false);
    setMentionSearch('');
  };

  const insertMention = (user) => {
    if (!textareaRef.current || mentionStart === -1) return;

    const cursorPos = textareaRef.current.selectionStart;
    const textBefore = value.substring(0, mentionStart);
    const textAfter = value.substring(cursorPos);
    
    // Insert @username with a space after
    const newValue = `${textBefore}@${user.username} ${textAfter}`;
    onChange({ target: { value: newValue } });

    // Reset mention state
    setShowMentions(false);
    setMentionSearch('');
    setMentionStart(-1);
    
    // Focus back on textarea and position cursor after the mention
    setTimeout(() => {
      if (textareaRef.current) {
        const newCursorPos = mentionStart + user.username.length + 2; // +2 for @ and space
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
      }
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!showMentions || mentionUsers.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % mentionUsers.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + mentionUsers.length) % mentionUsers.length);
        break;
      case 'Enter':
        if (showMentions) {
          e.preventDefault();
          insertMention(mentionUsers[selectedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowMentions(false);
        break;
      default:
        break;
    }
  };

  // Scroll selected item into view
  useEffect(() => {
    if (mentionListRef.current && showMentions) {
      const selectedElement = mentionListRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex, showMentions]);

  return (
    <div className="relative">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleTextChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className={className}
        rows={rows}
        autoFocus={autoFocus}
      />

      {/* Mention Autocomplete Dropdown */}
      {showMentions && mentionUsers.length > 0 && (
        <div 
          ref={mentionListRef}
          className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 max-h-60 overflow-y-auto z-50"
        >
          <div className="p-2 border-b border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-600 font-medium">
              Mention someone with @{mentionSearch}
            </p>
          </div>
          <ul className="py-1">
            {mentionUsers.map((user, index) => (
              <li
                key={user.id}
                onClick={() => insertMention(user)}
                onMouseEnter={() => setSelectedIndex(index)}
                className={`px-4 py-2 cursor-pointer flex items-center gap-3 transition-colors ${
                  index === selectedIndex ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                {/* Avatar */}
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-8 h-8 rounded-full flex-shrink-0"
                />
                
                {/* User Info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user.name}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    @{user.username} • {user.role}
                  </p>
                </div>

                {/* Arrow hint */}
                {index === selectedIndex && (
                  <span className="text-xs text-gray-400">↵</span>
                )}
              </li>
            ))}
          </ul>
          <div className="p-2 border-t border-gray-100 bg-gray-50">
            <p className="text-xs text-gray-500">
              Use ↑↓ to navigate, ↵ to select, ESC to dismiss
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MentionTextarea;
